import React, { useState } from 'react';
import { Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { AddButton } from '@rd-web-markets/shared/dist/util/buttons';
import FolderFileDragContainer from '@rd-web-markets/shared/dist/folders_and_files/FolderFileDragContainer';
import FolderPathContainer from '@rd-web-markets/shared/dist/folders_and_files/FolderPathContainer';
import CreateFolderOrFileModal from '@rd-web-markets/shared/dist/folders_and_files/modal/CreateFolderOrFileModal';
import MoveModal from '@rd-web-markets/shared/dist/folders_and_files/modal/MoveModal';
import { stopEvent } from '@rd-web-markets/shared/dist/util/domUtils';
import { useGetCompanyFoldersAndFiles } from './useGetCompanyFoldersAndFiles';

const CompanyFolderAndFileManagement = ({
  companyId,
  accountType,
  modelName,
  getFileUploadersAndVirtualFolderPath,
  canEditExistingDocumentsOrFolders = true,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [setFolders, setFiles, setCurrentFolderId, currentFolderId, folders, files, currentPath] =
    useGetCompanyFoldersAndFiles(companyId);

  const [createModalShow, setCreateModalShow] = useState(false);
  const [moveModalShow, setMoveModalShow] = useState(false);
  const [modalRoute, setModalRoute] = useState('');
  const [draggedElement, setDraggedElement] = useState({});
  const [currentAction, setCurrentAction] = useState(null);
  const [droppedElement, setDroppedElement] = useState({});

  const goToFolderQuery = (folderId) => {
    const base = `/${accountType}/companies/${companyId}/documents`;
    history.push(folderId ? `${base}?folder_id=${folderId}` : base);
  };

  const handleDragEnter = (e) => {
    stopEvent(e, false);
    if (e.dataTransfer.types.includes('Files')) showCreateModal('folder_files');
  };

  const showCreateModal = (route) => {
    setCreateModalShow(true);
    setModalRoute(route);
  };

  const hideAndEmptyElements = () => {
    setMoveModalShow(false);
    setDraggedElement({});
    setDroppedElement({});
  };

  const isFile = (el) => el.hasOwnProperty('download_link');

  const showFolderOrFileDropModal = (draggedEl, droppedEl) => {
    setDroppedElement({ ...droppedEl });

    const isDraggedFile = isFile(draggedEl);
    const draggedFileIsOverItsFolder = isDraggedFile && draggedEl.folder_id === droppedEl.id;

    if (
      !draggedEl.name ||
      !droppedEl.name ||
      isFile(droppedEl) ||
      draggedEl.id === droppedEl.id ||
      draggedFileIsOverItsFolder ||
      (!isDraggedFile && draggedEl.parent_folder_id === droppedEl.id) ||
      draggedEl.virtual_folder
    ) return;

    setMoveModalShow(true);
  };

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragOver={(e) => stopEvent(e, false)}
      onDragLeave={(e) => stopEvent(e, false)}
      onDrop={(e) => stopEvent(e, false)}
      data-testid="dropZone"
    >
      <FolderPathContainer
        goToFolderQuery={goToFolderQuery}
        path={currentPath}
        setCurrentFolderId={setCurrentFolderId}
        modelName={modelName}
        showFolderOrFileDropModal={showFolderOrFileDropModal}
        draggedElement={draggedElement}
        setDraggedElement={setDraggedElement}
        setCurrentAction={setCurrentAction}
        getFileUploadersAndVirtualFolderPath={getFileUploadersAndVirtualFolderPath}
        userCanDrag={canEditExistingDocumentsOrFolders}
        showPath={true}
      />
      <hr />

      <CreateFolderOrFileModal
        route={modalRoute}
        show={createModalShow}
        onHide={() => setCreateModalShow(false)}
        currentFolderId={currentFolderId}
        allData={modalRoute === 'folders' ? folders : files}
        setAllData={modalRoute === 'folders' ? setFolders : setFiles}
        targetModelId={companyId}
        type="Company"
      />

      <MoveModal
        show={moveModalShow}
        hideAndEmptyElements={hideAndEmptyElements}
        draggedElement={draggedElement}
        droppedElement={droppedElement}
        route={isFile(draggedElement) ? 'folder_files' : 'folders'}
        allData={isFile(draggedElement) ? files : folders}
        setAllData={isFile(draggedElement) ? setFiles : setFolders}
        isFile={isFile}
      />

      <Row>
        <AddButton
          variant="primary"
          onClick={() => showCreateModal('folders')}
          disabled={false}
          className="ms-auto me-3"
          text={t('folder')}
        />
        <AddButton
          variant="primary"
          onClick={() => showCreateModal('folder_files')}
          disabled={false}
          className="me-3"
          text={t('file')}
        />
      </Row>

      <FolderFileDragContainer
        goToFolderQuery={goToFolderQuery}
        folders={folders}
        files={files}
        setFiles={setFiles}
        setFolders={setFolders}
        setCurrentFolderId={setCurrentFolderId}
        showFolderOrFileDropModal={showFolderOrFileDropModal}
        draggedElement={draggedElement}
        setDraggedElement={setDraggedElement}
        currentAction={currentAction}
        setCurrentAction={setCurrentAction}
        canEditExistingDocumentsOrFolders={canEditExistingDocumentsOrFolders}
      />
    </div>
  );
};

export default CompanyFolderAndFileManagement;
